import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, Circle } from 'lucide-react';
import { formatWhen } from '@/lib/format';
import { ECMR_STATUS_LABEL, useEcmrList, type EcmrRole, type EcmrStatus } from '@/api/hooks/ecmr';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Field';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';

const ROLE_SHORT: Record<EcmrRole, string> = { sender: 'изпращач', carrier: 'превозвач', consignee: 'получател' };

export function EcmrList() {
  const [status, setStatus] = useState<EcmrStatus | ''>('');
  const { data, isLoading, isError, refetch } = useEcmrList(status || undefined);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-ink-dim">
          Електронната ЧМР се попълва сама от заявката и се подписва на телефона на шофьора — на товарене от изпращача и
          шофьора, на разтоварване от получателя.
        </p>
        <Select
          className="ml-auto w-56"
          aria-label="Състояние"
          value={status}
          onChange={(e) => setStatus(e.target.value as EcmrStatus | '')}
          options={[
            { value: '', label: 'Всички активни' },
            { value: 'draft', label: ECMR_STATUS_LABEL.draft },
            { value: 'issued', label: ECMR_STATUS_LABEL.issued },
            { value: 'delivered', label: ECMR_STATUS_LABEL.delivered },
            { value: 'cancelled', label: ECMR_STATUS_LABEL.cancelled },
          ]}
        />
      </div>
      <Card className="overflow-hidden">
        {isLoading ? (
          <LoadingBlock />
        ) : isError ? (
          <ErrorState onRetry={() => void refetch()} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR>
                  <TH>ЧМР</TH>
                  <TH>Заявка</TH>
                  <TH>Маршрут</TH>
                  <TH>Подписи</TH>
                  <TH>Състояние</TH>
                  <TH>Последна промяна</TH>
                </TR>
              </THead>
              <TBody>
                {(data ?? []).length === 0 ? (
                  <TableEmpty colSpan={6}>
                    Още няма електронни ЧМР. Създава се сама, когато шофьорът подпише на товарене или когато отворите
                    заявка.
                  </TableEmpty>
                ) : (
                  (data ?? []).map((row) => (
                    <TR key={row.id}>
                      <TD className="font-mono text-sm">{row.number}</TD>
                      <TD>
                        <Link to="/orders/$orderId" params={{ orderId: row.orderId }} className="font-mono text-sm text-accent hover:underline">
                          {row.orderNumber}
                        </Link>
                      </TD>
                      <TD className="max-w-[18rem] truncate text-ink-dim">{row.route}</TD>
                      <TD>
                        <div className="flex gap-2 text-xs">
                          {(['sender', 'carrier', 'consignee'] as EcmrRole[]).map((r) => (
                            <span key={r} className="inline-flex items-center gap-1">
                              {row.signed.includes(r) ? <CheckCircle2 className="size-3.5 text-ok" /> : <Circle className="size-3.5 text-ink-faint" />}
                              {ROLE_SHORT[r]}
                            </span>
                          ))}
                        </div>
                      </TD>
                      <TD>
                        <Badge tone={row.status === 'delivered' ? 'ok' : row.status === 'issued' ? 'accent' : row.status === 'cancelled' ? 'danger' : 'warn'}>
                          {ECMR_STATUS_LABEL[row.status]}
                        </Badge>
                      </TD>
                      <TD className="whitespace-nowrap text-ink-dim">{formatWhen(row.updatedAt)}</TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
